/*
  barna flow — S3 product page behaviour (templates/product.s3.json).

  Three small pieces, each doing only what Liquid and CSS cannot:
    s3-media       mobile snap carousel sync + zoom lightbox (desktop is a CSS mosaic).
    s3-tabs        Arrae's material explorer. Without JS every panel is already visible.
    s3-rise        the gentle rise-on-scroll from brand.md → Motion.
*/
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const smooth = () => (reduced.matches ? 'auto' : 'smooth');

  /* ---------------------------------------------------------------- media */
  if (!customElements.get('s3-media')) {
    customElements.define(
      's3-media',
      class S3Media extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('.s3-media__track');
          this.frames = Array.from(this.querySelectorAll('.s3-media__frame'));
          this.dots = Array.from(this.querySelectorAll('[data-media-go]'));
          this.dialog = this.querySelector('.s3-lightbox');
          this.zooms = Array.from(this.querySelectorAll('[data-zoom-src]'));
          this.index = 0;
          if (!this.track || this.frames.length === 0) return;

          this.dots.forEach((dot) => {
            dot.addEventListener('click', () => this.go(Number(dot.dataset.mediaGo)));
          });

          if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting) this.mark(this.frames.indexOf(entry.target));
                });
              },
              { root: this.track, threshold: 0.6 }
            );
            this.frames.forEach((frame) => observer.observe(frame));
          }

          this.initLightbox();
        }

        go(index) {
          const next = (index + this.frames.length) % this.frames.length;
          this.track.scrollTo({ left: this.frames[next].offsetLeft, behavior: smooth() });
          this.mark(next);
        }

        mark(index) {
          if (index < 0 || index === this.index) return;
          this.index = index;
          this.dots.forEach((dot, i) => {
            if (i === index) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        }

        initLightbox() {
          if (!this.dialog || this.zooms.length === 0 || typeof this.dialog.showModal !== 'function') return;
          this.img = this.dialog.querySelector('.s3-lightbox__img');
          this.zoomIndex = 0;

          this.zooms.forEach((button, i) => {
            button.addEventListener('click', () => {
              this.show(i);
              this.dialog.showModal();
            });
          });
          this.dialog.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.dialog.close());
          this.dialog.querySelectorAll('[data-lightbox-step]').forEach((button) => {
            button.addEventListener('click', () => this.show(this.zoomIndex + Number(button.dataset.lightboxStep)));
          });
          this.dialog.addEventListener('click', (event) => {
            if (event.target === this.dialog) this.dialog.close();
          });
          this.dialog.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') this.show(this.zoomIndex + 1);
            if (event.key === 'ArrowLeft') this.show(this.zoomIndex - 1);
          });
        }

        show(index) {
          const count = this.zooms.length;
          this.zoomIndex = (index + count) % count;
          const button = this.zooms[this.zoomIndex];
          this.img.src = button.dataset.zoomSrc;
          this.img.alt = button.getAttribute('aria-label') || '';
        }
      }
    );
  }

  /* ----------------------------------------------------------------- tabs */
  const initTabs = (root) => {
    if (root.dataset.s3Init) return;
    root.dataset.s3Init = 'true';
    const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    const panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));
    if (tabs.length === 0 || panels.length === 0) return;

    const select = (index) => {
      tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(i));
      tab.addEventListener('keydown', (event) => {
        const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        const next = (i + step + tabs.length) % tabs.length;
        select(next);
        tabs[next].focus();
      });
    });
    select(0);
  };

  /* ----------------------------------------------------------------- rise */
  const initRise = () => {
    if (reduced.matches || !('IntersectionObserver' in window)) return;
    const items = document.querySelectorAll('.s3-rise:not([data-s3-rise])');
    if (items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    items.forEach((item) => {
      item.dataset.s3Rise = 'true';
      observer.observe(item);
    });
  };

  const initAll = () => {
    document.querySelectorAll('[data-s3-tabs]').forEach(initTabs);
    initRise();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
